import React, {useEffect, useState, useRef} from 'react';
import cx from 'classnames';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import {useRouter} from 'next/router';
import _ from 'lodash';

import DocumentHeader from '../../components/DocumentHeader';
import SignupModal from '../../components/SignupModal';
import Button, {ButtonTypes} from '../../components/ButtonNew';
import Breadcrumbs from '../../components/Breadcrumbs';
import {Breadcrumb as BreadcrumbMetadata} from '../../components/LinkedData';
import FollowButton from '../../components/FollowButton';
import QuestionList from './QuestionList';
import QuestionListSort from './QuestionListSort';
import AddQuestionModal from './AddQuestionModal';
import SuggestedQueries from './SuggestedQueries';
import Collections from './Collections';
import RelatedItems from './RelatedItems';
import getBreadcrumbs from './util';
import DropdownInput from '../../components/DropdownInput';
import {LockIcon, SearchIcon} from '../../components/svg';
import Link from '../../components/Link';
import FadeOut from '../../components/FadeOut';
import {
  getQuestions, getTags, saveQuestion, unsaveQuestion,
} from '../../api/api';
import {Feature} from '../../../../lib/constants/tags';

import {
  createURLWithParams,
  tagsToQuestionListTitle,
  tagsToQuestionListDescription,
  getFirstValue,
  tagToOption,
  roleToRoleCategoryOptions,
} from '../../util';
import {QUERY_PARAMS, DIFFICULTY_LEVELS} from '../../constants/questions';
import {useAppContext, useAnalytics, useNotifications} from '../../hooks';
import {PermissionType} from '../../../../lib/constants/permissions';
import {getCanonicalURL} from '../../util/seo';

const defaultPageDescription = `Explore thousands of real interview questions asked by tech companies.
Practice your interview skills by answering questions and receiving feedback.`;

const getCanonicalParams = (page) => {
  const params = ['company', 'role', 'type', 'query', 'stage', 'difficulty', 'filterBy'];
  if (page > 1) params.push('page');
  return params;
};

const QuestionListPage = ({initialQuestions, tagMap, initialTags}) => {
  const router = useRouter();
  const {query, asPath: path} = router;
  const params = _.pick(query, QUERY_PARAMS);

  const {currentUser, hasSubscription, hasPermission} = useAppContext();
  const {tracker} = useAnalytics();
  const {setSuccess} = useNotifications();

  const [loading, setLoading] = useState(false);
  const [gatingModal, setGatingModal] = useState(false);
  const [tags, setTags] = useState(() => initialTags || {
    role: [],
    company: [],
    type: [],
  });
  const [questions, setQuestions] = useState(() => initialQuestions || {
    pagination: {
      hasMore: false,
      current: params.page || 1,
      limit: 0,
      count: 0,
    },
    results: [],
  });
  const isAdmin = currentUser && currentUser.role === 'admin';
  const [selectedRoleTag, setSelectedRoleTag] = useState(null);
  const [selectedCategoryTag, setSelectedCategoryTag] = useState(null);
  const [selectedCompanyTag, setSelectedCompanyTag] = useState(null);

  const [isScrolled, setIsScrolled] = useState(false);
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const isInitialQuestions = useRef(true);
  const isInitialTags = useRef(true);
  const contentOffset = contentRef?.current?.offsetTop - headerRef?.current?.offsetHeight || 0;

  const showGatingModal = (id) => {
    if (!currentUser) {
      tracker.questionListViewSignupWall({questionId: id});
      setGatingModal(true);
    }
  };

  const scrollToTopIfNeeded = () => {
    if (isScrolled) {
      window.scrollTo({top: contentOffset});
    }
  };

  const handleDropdownSelection = (target) => (val) => {
    if (!currentUser) {
      showGatingModal();
    } else {
      const url = createURLWithParams(
        '/questions',
        {
          ...params,
          page: 1,
          [target]: val ? [val] : null, // This is an array because we can support multiselect
          ...(target === 'filterBy' && val === 'pending' ? {sortBy: 'newest'} : {}), // sort by newest when filter by pending
          ...(target === 'filterBy' && val !== 'codeEditor' ? {difficulty: undefined} : {}), // Remove 'difficulty' param if filterBy is not 'codeEditor'
        },
        {arrayFormat: 'comma'},
      );
      router.push(url, null, {shallow: true});
      scrollToTopIfNeeded();
    }
  };

  const handleCompanyDropdownClick = () => {
    if (!hasSubscription) {
      tracker.questionListCompanyFilterUpsell();
      router.push('/upgrade?tag=company-filter&src=company-filter');
    }
  };

  const handleDifficultyDropdownClick = () => {
    if (!hasSubscription) {
      tracker.questionListDifficultyFilterUpsell();
      router.push('/upgrade?tag=difficulty-filter&src=difficulty-filter');
    }
  };

  // Handles saving/upvoting questions
  const onSaveQuestion = async (id, save = true) => {
    if (save) {
      tracker.questionListClickSave({id, question: id});
      await saveQuestion(id);
      setSuccess('Question saved!', (
        <span>
          You can find this question later in your
          {' '}
          <Link className="font-semibold text-indigo" href="/questions?filterBy=saved">Saved Questions</Link>
          .
        </span>
      ));
    } else {
      unsaveQuestion(id);
    }
  };

  const onSearch = (search) => {
    tracker.questionListSearch({search});
    const url = createURLWithParams(
      '/questions',
      {
        ...params,
        page: 1,
        query: search.length >= 3 ? search : null,
      },
      {arrayFormat: 'comma'},
    );
    router.push(url);
  };

  // Handles search with debounced (delayed) input
  const onSearchDebounced = _.debounce(onSearch, 750, {maxWait: 2000});

  // Load questions
  useEffect(() => {
    if (!router.isReady) return;

    tracker.questionListView({...params, search: params.query});

    // Avoid double-fetch on first load since this page is SSR
    if (isInitialQuestions.current) {
      isInitialQuestions.current = false;
      return;
    }

    setLoading(true);

    getQuestions(params).then(({results, pagination}) => {
      setQuestions({
        results,
        pagination,
        count: pagination.count,
      });
      setLoading(false);
    });
  }, [path, router.isReady]);

  // Sticky header styling effect
  useEffect(() => {
    const handleScroll = _.throttle(() => {
      setIsScrolled(window.scrollY > contentOffset);
    }, 20);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [contentOffset]);

  let pageTitle = 'Interview Questions';
  let seoTitle = 'Interview Questions';
  let pageDescription = defaultPageDescription;

  if (tags && params && questions) {
    pageTitle = tagsToQuestionListTitle(tags, params, false);
    seoTitle = `${pageTitle} (Updated ${new Date().getFullYear()})`;
    pageDescription = tagsToQuestionListDescription(tags, params, questions.count);
  }

  const selectedRole = params.role;
  const selectedCompany = params.company;
  const selectedCategory = params.type; // can have multiple types i.e. array of types
  const selectedDifficulty = params.difficulty;
  const selectedFilterBy = params.filterBy;

  // Load tags
  useEffect(() => {
    // Avoid double-fetch on first load since this page is SSR
    if (isInitialTags.current) {
      isInitialTags.current = false;
      return;
    }

    const getAndSetTags = async () => {
      const t = await getTags({
        feature: Feature.Questions,
        ...(selectedRole ? {role: selectedRole} : {}),
        ...(selectedCategory ? {category: selectedCategory} : {}),
        ...(selectedCompany ? {company: selectedCompany} : {}),
      });
      setTags(t);
    };
    getAndSetTags();
  }, [selectedRole, selectedCategory, selectedCompany]);

  useEffect(() => {
    setSelectedRoleTag(null);
    setSelectedCategoryTag(null);
    setSelectedCompanyTag(null);
    if (selectedRole) setSelectedRoleTag(_.find(tags.role, ['id', getFirstValue(selectedRole)]));
    if (selectedCategory) setSelectedCategoryTag(_.find(tags.type, ['id', getFirstValue(selectedCategory)]));
    if (selectedCompany) setSelectedCompanyTag(_.find(tags.company, ['id', getFirstValue(selectedCompany)]));
  }, [tags, selectedRole, selectedCategory, selectedCompany]);

  const roles = roleToRoleCategoryOptions(tags.role.filter((r) => r.count), tags['role-category']);
  const companies = tags.company.filter((c) => c.count).map(tagToOption);
  const categories = tags.type.filter((t) => t.count).map(tagToOption);
  const filterByOptions = [
    ...(hasPermission(PermissionType.QUESTION_PUBLISH) ? [{label: '⏳  Pending', value: 'pending'}] : []),
    {label: '🧠  Expert Answers', value: 'expert'},
    {label: '🎥  Videos', value: 'video'},
    {label: '💻  Code Editor', value: 'codeEditor'},
    ...(currentUser ? [{label: '💾  Saved', value: 'saved'}] : []),
  ];
  const breadcrumbs = getBreadcrumbs(tags, selectedCompany, selectedRole, selectedCategory);

  const onClickFollowButton = (following) => (following
    ? tracker.questionListFollowTag({follower: currentUser.id, tag: selectedCompanyTag.id})
    : tracker.questionListUnfollowTag({follower: currentUser.id, tag: selectedCompanyTag.id})
  );

  const onClickRelatedItem = (item) => {
    tracker.questionListClickRelatedItem({[item.type]: item.id});
    scrollToTopIfNeeded();
    showGatingModal();
  };

  const showCollections = !(selectedRole || selectedCategory
    || selectedCompany || selectedFilterBy);

  return (
    <div className="bg-white min-h-screen">
      {/* Metadata */}
      <DocumentHeader
        title={seoTitle}
        canonicalUrl={getCanonicalURL(path, getCanonicalParams(params.page))}
        metaDescription={pageDescription}
        socialDescription={pageDescription}
        prerender404={questions.count === 0}
      />
      {breadcrumbs && <BreadcrumbMetadata items={breadcrumbs} />}

      {/* Modals */}
      <SignupModal isOpen={gatingModal} source="socrates" />
      <AddQuestionModal />

      {/* Title and Description */}
      <div className="mx-auto max-w-6xl px-4 pt-4 md:pt-7 flex flex-col">
        {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
        <div className="flex justify-between items-center flex-row">
          <div className="flex-grow">
            <h1 className="block mt-2 mb-2 md:mt-3 text-left font-bold text-gray-900 text-2xl md:text-4xl tracking-tight leading-tight">
              {pageTitle}
            </h1>
            <span className="block w-full max-w-2xl mb-2 text-sm text-left md:text-base font-regular text-gray-600">
              {pageDescription}
            </span>
          </div>
          {selectedCompany && !loading && (
            <div className="hidden ml-2 md:flex gap-2">
              <FollowButton
                tag={selectedCompanyTag}
                succinct={false}
                className="rounded-lg whitespace-nowrap"
                onClick={onClickFollowButton}
              />
              <Button
                href={isAdmin ? '/questions/add' : `/questions/contribute?company=${selectedCompany}`}
                className="rounded-lg whitespace-nowrap hidden lg:block"
                onClick={() => {
                  if (!isAdmin) tracker.questionListClickContribute({button: 'top'});
                }}
              >
                + Add interview
              </Button>
            </div>
          )}
          {!selectedCompany && (
            <Button
              href={isAdmin ? '/questions/add' : '/questions/contribute'}
              className="rounded-lg whitespace-nowrap hidden lg:block"
              onClick={() => {
                if (!isAdmin) tracker.questionListClickContribute({button: 'top'});
              }}
            >
              + Add interview
            </Button>
          )}
        </div>
      </div>

      {/* Sticky Filter + Related Items Bar */}
      <div
        className={cx('sticky top-0 pt-3 pb-2 md:pb-2 bg-white z-10 border-b border-gray-200', {'shadow-sm': isScrolled})}
        ref={headerRef}
      >
        {/* Filters */}
        <div className="mx-auto max-w-6xl px-4 flex flex-col">
          <div className="flex flex-wrap items-start justify-between">
            <div className="-mx-4 pl-4 gap-x-2 flex items-start overflow-x-scroll scrollbar-hidden md:overflow-x-visible">
              <DropdownInput
                placeholder="Role"
                options={roles}
                value={selectedRole}
                onChange={handleDropdownSelection('role')}
                showFilter
                truncateLength={13}
              />
              <OverlayTrigger
                placement="bottom"
                overlay={hasSubscription
                  ? <></>
                  : <Tooltip>Filtering by company is only available to Exponent members</Tooltip>}
              >
                {/* eslint-disable-next-line max-len */}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div onClick={handleCompanyDropdownClick}>
                  <DropdownInput
                    placeholder={hasSubscription ? 'Company' : (
                      <span>
                        <LockIcon className="h-3.5 w-3.5 mb-0.5" />
                        {' '}
                        Company
                      </span>
                    )}
                    options={companies}
                    value={selectedCompany}
                    onChange={handleDropdownSelection('company')}
                    showFilter
                    disabled={!hasSubscription}
                    truncateLength={13}
                  />
                </div>
              </OverlayTrigger>
              <DropdownInput
                placeholder="Category"
                options={categories}
                value={selectedCategory}
                onChange={handleDropdownSelection('type')}
                showFilter
                truncateLength={13}
              />
              <DropdownInput
                placeholder="Filter"
                options={filterByOptions}
                value={selectedFilterBy}
                onChange={handleDropdownSelection('filterBy')}
                truncateLength={13}
              />
              {selectedFilterBy === 'codeEditor' && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={hasSubscription
                    ? <></>
                    : (
                      <Tooltip>
                        Filtering by difficulty is only available to Exponent members
                      </Tooltip>
                    )}
                >
                  {/* eslint-disable-next-line max-len */}
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <div onClick={handleDifficultyDropdownClick}>
                    <DropdownInput
                      placeholder={hasSubscription ? 'Difficulty' : (
                        <span>
                          <LockIcon className="h-3.5 w-3.5 mb-0.5" />
                          {' '}
                          Difficulty
                        </span>
                      )}
                      options={DIFFICULTY_LEVELS}
                      value={selectedDifficulty}
                      onChange={handleDropdownSelection('difficulty')}
                      disabled={!hasSubscription}
                    />
                  </div>
                </OverlayTrigger>
              )}
            </div>
            <div className="hidden mb-1 md:flex gap-x-2">
              <QuestionListSort
                isGated={!currentUser}
                onClickPaywalledContent={showGatingModal}
              />
              <Button
                href={isAdmin ? '/questions/add' : '/questions/contribute'}
                className="rounded-lg lg:hidden"
                onClick={() => {
                  if (!isAdmin) tracker.questionListClickContribute({button: 'top'});
                }}
              >
                Add interview
              </Button>
            </div>
          </div>
          <div className="w-full flex flex-col block lg:hidden">
            {/* Mobile-only Collections */}
            {showCollections && (
              <div className="mt-3">
                <Collections
                  path={path}
                  currentUser={currentUser}
                  trackerEvent={tracker.questionListClickCollection}
                />
              </div>
            )}

            {/* Mobile-only Related Items Bar */}
            <FadeOut className="mt-2 -mx-4" right left>
              <div className="w-full px-4 my-1 overflow-x-scroll scrollbar-hidden">
                <RelatedItems
                  company={selectedCompanyTag}
                  role={selectedRoleTag}
                  category={selectedCategoryTag}
                  tags={tags}
                  containerClass="flex -mx-4 px-4 gap-2"
                  showTitle={false}
                  maxPills={10}
                  onClick={onClickRelatedItem}
                />
              </div>
            </FadeOut>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-6xl px-4 py-6 flex flex-col" ref={contentRef}>
        <div className="flex">
          {/* Center */}
          <div className="max-w-3xl w-full">
            {/* Collections */}
            {showCollections && (
              <div className="mb-6 hidden lg:block">
                <Collections
                  path={path}
                  currentUser={currentUser}
                  trackerEvent={tracker.questionListClickCollection}
                />
              </div>
            )}

            {/* Main List */}
            <QuestionList
              questions={questions}
              isGated={!currentUser}
              isLoading={loading}
              onSave={currentUser ? onSaveQuestion : showGatingModal}
              onClickPaywalledContent={showGatingModal}
              query={query}
              tagMap={tagMap}
            />
          </div>

          {/* Right Sidebar */}
          <div className="hidden lg:flex flex-col gap-y-4 w-full ml-6 max-w-xs">
            <div className="sticky top-24 flex flex-col gap-y-4">
              {/* Search Bar */}
              <div className="relative">
                <input
                  className={cx(`
                w-full
                rounded-lg
                shadow-sm
                border
                border-gray-300
                pl-10
                py-3
                leading-5
                focus:outline-none
                focus:border-gray-500
              `, {
                    'text-gray-300 placeholder-gray-400 bg-white': !currentUser,
                  })}
                  placeholder="Search for questions, companies..."
                  disabled={!currentUser}
                  onChange={(e) => onSearchDebounced(e.target.value)}
                  defaultValue={params.query}
                />
                <SearchIcon className="absolute m-auto top-0 bottom-0 left-3 h-5 w-5 pointer-events-none" />
              </div>

              {/* Related Items */}
              <div className="border border-gray-200 rounded-lg p-4">
                <RelatedItems
                  company={selectedCompanyTag}
                  role={selectedRoleTag}
                  category={selectedCategoryTag}
                  tags={tags}
                  onClick={onClickRelatedItem}
                />
              </div>

              {/* Interview Recently? */}
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-base font-semibold mb-2">Interviewed recently?</p>
                <p className="font-medium text-gray-500 mb-3">
                  Help improve our question database (and earn karma)
                  by telling us about your experience
                </p>
                <Button
                  href={isAdmin ? '/questions/add' : '/questions/contribute'}
                  className="rounded-lg transition duration-300"
                  buttonType={isScrolled ? ButtonTypes.PRIMARY : ButtonTypes.SECONDARY}
                  onClick={() => {
                    if (!isAdmin) tracker.questionListClickContribute({button: 'sidebar'});
                  }}
                >
                  + Add your interview
                </Button>
              </div>

              {/* Trending Companies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-base font-semibold mb-2">Trending companies</p>
                <div className="flex flex-wrap -mx-4 px-4 overflow-scroll scrollbar-hidden gap-2">
                  {tags?.company?.slice(0, 6).map((c) => (
                    <Link
                      href={createURLWithParams('/questions', {company: c.id})}
                      className="flex items-center border rounded-full px-2.5 py-2 cursor-pointer transition-all hover:border-gray-300"
                      onClick={() => tracker.questionListClickTrendingCompany({company: c.id})}
                      key={c.id}
                    >
                      {c.image_url && <img src={c.image_url} alt={c.name} className="w-4 h-4 rounded-full mr-1" />}
                      <span className="mr-1 font-medium text-xs">{c.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <SuggestedQueries
          company={selectedCompanyTag}
          role={selectedRoleTag}
          tags={tags}
          className="md:w-4/5"
        />
      </div>
    </div>
  );
};

export default QuestionListPage;
